import React, { SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  AddressInterface,
  CityInterface,
  OptionItemInterface,
  OrderInterface,
  OrderItem,
  PriceItemInterface,
  SaleItemInterface,
  StoreInterface,
  ValidationResultInterface,
  ValidationRulesInterface,
} from '../../../../../../common/types/interfaces'
import { getSalesApi } from '../../../../../../salesApi/salesApi'
import { useInput } from '../../../../../../common/hooks/useInput'
import { processAttributes } from '../../../../../../common/utils/functions/processAttributes'
import { useValidation } from '../../../../../../common/hooks/useValidation'
import { orderValidations } from '../../../../../../common/utils/constants'
import { useSelector } from 'react-redux'
import { selectCurrentCluster } from '../../../../../../store/slices/currentClusterSlice'
import { useCommonLoader } from '../../../../../../common/hooks/useCommonLoader'
import { delayAction } from '../../../../../../common/utils/functions/delayAction'
import { createSelectList } from '../../../../../../common/utils/functions/createSelectList'
import { createTypeSelectList } from '../../../../../../common/utils/functions/createTypeSelectList'
import { OrderStatus, DeliveryType, PaymentMethod } from '../../../../../../common/types/enums'
import {
  getDeliveryTypeText,
  getPaymentMethodText,
} from '../../../../../../common/utils/functions/enumToText'

// 👇 Единая карта переходов статусов
const orderTransitions: Record<number, number[]> = {
  [OrderStatus.NEW]: [OrderStatus.PREPARING, OrderStatus.CANCELLED],
  [OrderStatus.PREPARING]: [OrderStatus.READY, OrderStatus.CANCELLED],
  [OrderStatus.READY]: [OrderStatus.DELIVERING, OrderStatus.PICKED_UP, OrderStatus.CANCELLED],
  [OrderStatus.DELIVERING]: [OrderStatus.DELIVERED, OrderStatus.CANCELLED],
  [OrderStatus.DELIVERED]: [],
  [OrderStatus.PICKED_UP]: [],
  [OrderStatus.CANCELLED]: [],
}

const validations: ValidationRulesInterface[] = orderValidations

export const useForm = (
  initialData: OrderInterface,
  setData: React.Dispatch<SetStateAction<OrderInterface | undefined>>,
  edit: boolean,
  getList: () => void,
  setModalActive: React.Dispatch<SetStateAction<boolean>>,
  modalActive: boolean,
  data?: OrderInterface,
  el?: OrderInterface,
) => {
  const [salesApi] = useState(getSalesApi())
  const [disabled, setDisabled] = useState<boolean>(true)
  const [priceList, setPriceList] = useState<PriceItemInterface[]>([])
  const [saleItemList, setSaleItemList] = useState<SaleItemInterface[]>([])
  const [adding, setAdding] = useState(false)
  const { inputHandler } = useInput(setData)
  const [validationResult, setValidationResult] = useState<ValidationResultInterface[] | []>([])
  const listRef = useRef<HTMLDivElement>(null)
  const currentCluster = useSelector(selectCurrentCluster)
  const { showCommonLoader, hideCommonLoader } = useCommonLoader()
  const [storeList, setStoreList] = useState<StoreInterface[]>([])

  // 👇 Запоминаем исходный статус при открытии
  const [originalStatus, setOriginalStatus] = useState<number | undefined>(data?.status)

  // 👇 Обновляем originalStatus при изменении data (только при смене заказа!)
  useEffect(() => {
    if (data?.id) {
      setOriginalStatus(data.status)
    }
  }, [data?.id]) // убрали data.status из зависимостей!

  useEffect(() => {
    if (el) {
      setData(structuredClone(el))
      return
    }
    setData({ ...initialData })
  }, [el, setData, initialData])

  const { validate, checkAndAddValidation, setDirty } = useValidation(
    setValidationResult,
    validationResult,
    validations,
  )

  const getValidationResult = (fieldName: string) => {
    return validationResult.find((el) => el.name === fieldName)
  }

  const latestPriceMap = useMemo(() => {
    const map = new Map<number, number>()
    priceList?.forEach((p) => {
      const id = Number(p.saleItemId)
      const price = Number(p.price)
      const time = p.createdAt ? new Date(p.createdAt).getTime() : 0
      const storedTime = map.get(id)
      if (storedTime === undefined || time > storedTime) {
        map.set(id, price)
      }
    })
    return map
  }, [priceList])

  const saleItemMap = useMemo(
    () => new Map(saleItemList.map((item) => [item.id, item])),
    [saleItemList],
  )

  const getPriceList = useCallback(async () => {
    try {
      const result = await salesApi.getPriceList()
      if (result) setPriceList(result.data)
    } catch (error) {}
  }, [salesApi])

  const getSaleItemList = useCallback(async () => {
    try {
      const result = await salesApi.getSaleItemList()
      if (result) setSaleItemList(result?.data)
    } catch (error) {}
  }, [salesApi])

  const createSaleItemSelectList = useCallback(() => {
    let arr: OptionItemInterface[] = []
    for (const item of saleItemList) {
      const attrsString = processAttributes(item.attributes)
      arr.push({
        id: String(item.id),
        label: '#' + item.id + ' ' + item.name + attrsString,
        name: item.name,
        value: String(item.id),
      })
    }
    return arr
  }, [saleItemList])

  // 👇 НОВАЯ ФУНКЦИЯ: список типов доставки
  const getDeliveryTypeOptions = useCallback(() => {
    return createTypeSelectList(DeliveryType, getDeliveryTypeText)
  }, [])

  // 👇 НОВАЯ ФУНКЦИЯ: список способов оплаты (ONLINE заблокирован)
  const getPaymentMethodOptions = useCallback(() => {
    const allOptions = createTypeSelectList(PaymentMethod, getPaymentMethodText)
    return allOptions.map((option) => ({
      ...option,
      disabled: Number(option.value) === PaymentMethod.ONLINE,
    }))
  }, [])

  const formAction = useCallback(async () => {
    try {
      if (currentCluster) {
        await showCommonLoader()
        await delayAction(1000)
        if (data?.id) {
          const {
            createdAt,
            iiko_id,
            c1_id,
            moysklad_id,
            updatedAt,
            uuid,
            fullAddress,
            store,
            user,
            bonusesAccrued,
            tasks,
            ...rest
          } = data

          const cleanData = { ...rest }

          delete cleanData.baseAddress?.id
          delete cleanData.baseAddress?.address?.id
          delete cleanData.baseAddress?.address?.isDirty
          delete cleanData.baseAddress?.city?.isDirty
          if (cleanData.fullAddressId === null) {
            delete cleanData.fullAddressId
          }
          if (cleanData.storeId === null) {
            delete cleanData.storeId
          }
          if (cleanData.items) {
            cleanData.items = cleanData.items.map(({ id, ...item }) => item)
          }
          await salesApi.updateOrder(cleanData)
        } else if (data) {
          const { items, ...rest } = data
          const cleanData = { ...rest }
          if (cleanData.baseAddress?.city) {
            delete cleanData.baseAddress.city.isDirty
          }
          if (cleanData.baseAddress?.address) {
            delete cleanData.baseAddress.address.isDirty
          }
          await salesApi.createOrder({
            ...cleanData,
            clusterId: currentCluster.id,
            items: items?.map(({ id, ...item }) => item) || [],
          })
        }
        await getList()
        await hideCommonLoader()
        setModalActive(false)
      }
    } catch (error) {
      await hideCommonLoader()
    }
  }, [salesApi, data, getList, setModalActive, currentCluster, showCommonLoader, hideCommonLoader])

  const deleteItem = useCallback(
    (itemId: number) => {
      setData((prev) => {
        if (!prev) return prev

        const items = prev.items?.filter((item) => item.itemId !== itemId) || []
        const subtotal = items.reduce((sum, item) => sum + item.total, 0)
        const total = subtotal + (prev.deliveryFee || 0)

        return { ...prev, items, subtotal, total }
      })
    },
    [setData],
  )

  const addItem = useCallback(
    (saleItemId: number) => {
      const saleItem = saleItemMap.get(saleItemId)

      setData((prev) => {
        if (!prev) return prev
        const newItem = {
          id: (prev.items?.reduce((max, item) => Math.max(max, item.itemId || 0), 0) || 0) + 1,
          itemId: saleItemId,
          categoryId: saleItem?.categoryId || -1,
          name: saleItemMap.get(saleItemId)?.name || `Товар #${saleItemId}`,
          price: latestPriceMap.get(saleItemId) || 0,
          quantity: 1,
          total: latestPriceMap.get(saleItemId) || 0,
        }

        const updatedItems = [...(prev.items || []), newItem]
        const subtotal = updatedItems.reduce((sum, item) => sum + item.total, 0)
        const total = subtotal + (prev.deliveryFee || 0)

        return {
          ...prev,
          items: updatedItems,
          subtotal,
          total,
        }
      })
      setAdding(false)
    },
    [setData, latestPriceMap, saleItemMap],
  )

  const updateItem = useCallback(
    (itemId: number, field: keyof OrderItem, value: string | number) => {
      setData((prev) => {
        if (!prev || !prev.items) return prev

        const updatedItems = prev.items.map((item) => {
          if (item.itemId !== itemId) return item

          let processedValue = value
          let numericValue = 0

          if (field === 'quantity' || field === 'price') {
            const strVal = String(value)
            let filtered = strVal
              .replace(field === 'price' ? /[^\d.]/g : /\D/g, '')
              .replace(/\.(?=.*\.)/g, '')
              .replace(/^0+(?!\.|$)/, '')

            if (filtered.length > 10) filtered = filtered.slice(0, 10)

            processedValue = filtered
            numericValue = parseFloat(filtered || '0') || 0
          }

          const updated = { ...item, [field]: processedValue }

          if (field === 'quantity' || field === 'price') {
            const priceNum = field === 'price' ? numericValue : item.price
            const quantityNum = field === 'quantity' ? numericValue : item.quantity
            updated.total = priceNum * quantityNum
          }

          return updated
        })

        const subtotal = updatedItems.reduce((sum, item) => sum + item.total, 0)
        const total = subtotal + (prev.deliveryFee || 0)

        return {
          ...prev,
          items: updatedItems,
          subtotal,
          total,
        }
      })
    },
    [setData],
  )

  const handleNumberInput = useCallback(
    (itemId: number, field: 'quantity' | 'price', value: string) => {
      updateItem(itemId, field, value)
    },
    [updateItem],
  )

  const handleDeliveryFeeInput = useCallback(
    (value: string) => {
      let filtered = value
        .replace(/[^\d.]/g, '')
        .replace(/\.(?=.*\.)/g, '')
        .replace(/^0+(?!\.|$)/, '')

      if (filtered.length > 10) filtered = filtered.slice(0, 10)

      const deliveryFeeNum = parseFloat(filtered || '0') || 0

      setData((prev) => {
        if (!prev) return prev

        const subtotal = prev.subtotal || 0
        const total = subtotal + deliveryFeeNum

        return {
          ...prev,
          deliveryFee: deliveryFeeNum,
          total,
        }
      })
    },
    [setData],
  )

  const handleAddItem = useCallback(
    (value: string | number | boolean, fieldName: string, type: string) => {
      if (type === 'add_item') {
        const id = Number(value)
        if (id > 0) addItem(id)
      }
    },
    [addItem],
  )

  const gMapInputHandler = useCallback(
    (inputData: { city: CityInterface; address: AddressInterface }) => {
      setData((prev) => {
        if (!prev) return prev

        return {
          ...prev,
          baseAddress: inputData,
        }
      })
    },
    [setData],
  )

  const getStoreList = useCallback(async () => {
    try {
      const result = await salesApi.getStoreList()
      if (result) setStoreList(result?.data || [])
    } catch (error) {
      console.error('Error loading stores:', error)
    }
  }, [salesApi])

  const createStoreSelectList = useCallback(() => {
    return createSelectList(storeList)
  }, [storeList])

  const getDisabledStatuses = useCallback(
    (hasStore: boolean): number[] => {
      if (!data?.id) {
        const allStatuses = Object.values(OrderStatus).filter(
          (v): v is number => typeof v === 'number',
        )
        return allStatuses.filter((s) => s !== OrderStatus.NEW)
      }

      if (originalStatus === undefined) return []

      const allowed = orderTransitions[originalStatus] || []
      const allStatuses = Object.values(OrderStatus).filter(
        (v): v is number => typeof v === 'number',
      )

      const disabled = allStatuses.filter((s) => s !== originalStatus && !allowed.includes(s))

      if (
        !hasStore &&
        originalStatus === OrderStatus.NEW &&
        !disabled.includes(OrderStatus.PREPARING)
      ) {
        disabled.push(OrderStatus.PREPARING)
      }

      return disabled
    },
    [data?.id, originalStatus],
  )

  useEffect(() => {
    getStoreList()
  }, [])

  useEffect(() => {
    if (!data) {
      setDisabled(true)
      return
    }

    const hasValidItems =
      data.items &&
      data.items.length > 0 &&
      data.items.every((item) => Number(item.price) > 0 && Number(item.quantity) > 0)

    const isUserOrder = !!data?.user
    const relevantValidationErrors = isUserOrder
      ? validationResult.filter((el) => el.name !== 'phone_number').some((el) => el.notValid)
      : validationResult.some((el) => el.notValid)

    // 👇 storeId теперь проверяем всегда
    const hasStoreId = !!data.storeId

    if (data.id) {
      setDisabled(
        !hasValidItems || relevantValidationErrors || !hasStoreId, // 👈 добавили
      )
    } else {
      const hasItems = data.items && data.items.length > 0
      const hasValidAddress = !!data.baseAddress?.address?.latitude
      const hasValidPhone = !!data.phone_number?.trim()
      const phoneValid =
        !validationResult.find((el) => el.name === 'phone_number' && el.notValid) && hasValidPhone

      setDisabled(
        !hasItems ||
          !hasValidAddress ||
          !phoneValid ||
          !hasValidItems ||
          relevantValidationErrors ||
          !hasStoreId, // 👈 добавили
      )
    }
  }, [data, validationResult])

  useEffect(() => {
    for (var item in data) {
      if (validations.map((el) => el.fieldName).includes(item)) {
        checkAndAddValidation(item)
      }
    }
  }, [checkAndAddValidation, data])

  useEffect(() => {
    for (var item in data) {
      if (validations.map((el) => el.fieldName).includes(item)) {
        validate(item, data[item])
      }
    }
  }, [data])

  useEffect(() => {
    getSaleItemList()
  }, [getSaleItemList])

  useEffect(() => {
    getPriceList()
  }, [getPriceList])

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTo({
        top: listRef.current.scrollHeight,
        behavior: 'smooth',
      })
    }
  }, [data?.items?.length])

  useEffect(() => {
    return () => {
      if (!modalActive) setData({ ...initialData })
    }
  }, [modalActive, initialData, setData])

  useEffect(() => {
    if (storeList.length === 1 && data && !data.storeId) {
      setData((prev) => {
        if (!prev) return prev
        return {
          ...prev,
          storeId: storeList[0].id,
        }
      })
    }
  }, [storeList])

  return {
    formAction,
    data,
    inputHandler,
    setDirty,
    getValidationResult,
    disabled,
    deleteItem,
    adding,
    setAdding,
    createSaleItemSelectList,
    handleAddItem,
    handleNumberInput,
    handleDeliveryFeeInput,
    gMapInputHandler,
    listRef,
    createStoreSelectList,
    getDisabledStatuses,
    // 👇 НОВЫЕ ФУНКЦИИ
    getDeliveryTypeOptions,
    getPaymentMethodOptions,
  }
}
